import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { findFontForStyle, FontStyle } from '@bynder-studio/structured-text';
import { Button, Dropdown, Form, Select } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { fontsElementsSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import generateTestId from 'packages/helpers/testIdHelpers';
import type { FontFamily as FontFamilyType } from '../../Shots/Text/Fonts/types';

type Props = {
    fontIdData: [string, boolean, string];
    rawFontIds: string[];
    onFontChange: (fontId: string) => void;
    disabled?: boolean;
    locked?: boolean;
};

// TODO: Make text fonts' components to use these generice ones after release
const FontFamilyBase = ({ fontIdData, rawFontIds, onFontChange, disabled, locked }: Props) => {
    const [, , mixedValue] = fontIdData;
    const [searchValue, setSearchValue] = useState('');
    const fonts: FontFamilyType[] = useSelector(fontsElementsSelector);
    const { translate } = useTranslate();

    const fontsList: Record<string, FontFamilyType> = useMemo(() => {
        const temp: Record<string, FontFamilyType> = {};

        fonts.forEach((font) => {
            temp[font.name] = font;
        });

        return temp;
    }, [fonts]);

    const isSelected = useCallback(
        (family: FontFamilyType) =>
            family.fonts.some((f) => f.referenceIds.some((referenceId) => rawFontIds.includes(String(referenceId)))),
        [rawFontIds],
    );

    const [selectedFamilyNames, familyName] = useMemo(() => {
        const selected = Object.values(fontsList).filter(isSelected);
        const names = new Set(selected.map((family) => family.name));
        const matched = selected[0] ?? fonts.find(isSelected);

        return [names, rawFontIds.length > 1 ? mixedValue : (matched?.name ?? 'Default')];
    }, [rawFontIds, fonts, fontsList, isSelected, mixedValue]);

    const isFontMissing = useMemo(() => {
        const known = fonts.flatMap((family) => family.fonts.flatMap((f) => f.referenceIds.map(String)));

        return rawFontIds.some((id) => !known.includes(id));
    }, [rawFontIds, fonts]);

    const familyListArray = useMemo(() => {
        if (searchValue === '') {
            return Object.values(fontsList);
        }

        return Object.values(fontsList).filter((f) => f.name.toLowerCase().includes(searchValue.toLowerCase()));
    }, [fontsList, searchValue]);

    const handleChange = useCallback(
        (family: FontFamilyType) => {
            const defaultFont = findFontForStyle(family.fonts, FontStyle.REGULAR) || family.fonts[0];

            onFontChange(defaultFont.id.toString());
        },
        [onFontChange],
    );

    return (
        <Form.Group>
            <Dropdown
                minWidth="288px"
                onClose={() => setSearchValue('')}
                {...generateTestId('shots_text_typeface')}
                trigger={({ isOpen, ...triggerProps }) => (
                    <Button
                        aria-label="shots_text_typeface"
                        isPressed={isOpen}
                        rightIcon={<Dropdown.Arrow />}
                        isDisabled={disabled || locked}
                        isFullWidth
                        {...triggerProps}
                    >
                        <Dropdown.SelectButtonText>{familyName}</Dropdown.SelectButtonText>
                    </Button>
                )}
            >
                <Select.SearchInput
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder={translate('editor.sidebar.shots.fonts.fontfamily.search')}
                    aria-label={translate('editor.sidebar.shots.fonts.fontfamily.search.label')}
                />
                {familyListArray.map((family) => (
                    <Dropdown.Item
                        key={family.name}
                        onClick={() => handleChange(family)}
                        isChecked={selectedFamilyNames.has(family.name)}
                    >
                        {family.name}
                    </Dropdown.Item>
                ))}
            </Dropdown>
            {isFontMissing && (
                <Form.HelperText icon={<Form.IconError />}>
                    {translate('editor.sidebar.shots.fonts.fontfamily.missing')}
                </Form.HelperText>
            )}
        </Form.Group>
    );
};

export { FontFamilyBase };
